import { Heading } from 'blaze-ui/Heading/Heading'
import Styles from './CreateNewModal.module.scss'
import { Modal, useModals } from 'almanac-ui/Modal'
import { Text } from 'blaze-ui/Text/Text'
import cx from 'classnames'
import { Calendar1, Design } from 'blaze-ui/icons'
import AddStrategies from 'blaze-ui/icons/16/AddStrategies'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { usePaywallGuard } from 'hooks/usePaywallGuard'
import { NewPostModal } from 'screens/Calendar/WeekViewV2/BatchContentActions/Modals/NewPostModal/NewPostModal'
import { CreateContentModal } from 'screens/Calendar/WeekViewV2/BatchContentActions/Modals/CreateContentModal/CreateContentModal'
import { useRootStore } from 'stores/RootStore'
import { observer } from 'mobx-react-lite'
import { generatePath, useHistory } from 'react-router-dom'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { CONTENT_PLAN_SCHEDULE_PATH } from 'paths'
import { CreateCampaignModal } from '../CreateCampaignModal/CreateCampaignModal'
import { AddStrategiesModal } from '../AddStrategiesModal/AddStrategiesModal'
import { useQueryClient } from 'react-query'
import { useFlipperFlag } from 'hooks/useFlipperFlag'

export const CreateNewModal = observer(() => {
  const queryClient = useQueryClient()
  const { openModal, closeAllModals } = useModals()
  const rootStore = useRootStore()
  const history = useHistory()
  const workspaceId = useCurrentWorkspaceId()
  const { data: brandPlan } = useCurrentBrandPlan()
  const canAddCampaign = brandPlan?.content_strategies && Object.keys(brandPlan.content_strategies).length > 0
  const isBlankPostEnabled = useFlipperFlag('blank_post_creation')
  const CreateModal = isBlankPostEnabled ? NewPostModal : CreateContentModal

  const guardedOpenAddMoreContentModal = usePaywallGuard(
    () => {
      openModal(CreateModal, {
        onSuccess: (postsCount: number, brandPlanScheduleId: number) => {
          rootStore.contentGeneration.setNumberOfNewPosts(postsCount)

          closeAllModals()
          history.push(generatePath(CONTENT_PLAN_SCHEDULE_PATH, { workspaceId, scheduleId: brandPlanScheduleId }))
        },
      })
    },
    { allowFreemium: true },
  )

  const handleCreateCampaign = () => {
    if (!brandPlan) return

    openModal(CreateCampaignModal, {
      brandPlan: brandPlan,
      onSuccess: () => {
        queryClient.invalidateQueries(['schedules', 'list'])
        queryClient.invalidateQueries(['workspaces', workspaceId, 'schedules', 'list'])
      },
    })
  }

  const handleCreateStrategy = () => {
    openModal(AddStrategiesModal)
  }

  return (
    <Modal.Root bodyClassName={Styles.body}>
      <Modal.Header title="Create New" isConfirmation />
      <Modal.Content withoutFooter isConfirmation>
        <div className={Styles.options}>
          <div className={Styles.option} onClick={guardedOpenAddMoreContentModal}>
            <div className={cx(Styles.icon, Styles.post)}>
              <Design size={20} color="var(--upgrade)" />
            </div>
            <div className={Styles.label}>
              <Heading level={5}>Post</Heading>
              <Text variant="secondary" color="var(--dark-60)">
                A single post that's added to a campaign
              </Text>
            </div>
          </div>
          {canAddCampaign && (
            <div className={Styles.option} onClick={handleCreateCampaign}>
              <div className={cx(Styles.icon, Styles.campaign)}>
                <Calendar1 size={20} color="var(--growth)" />
              </div>
              <div className={Styles.label}>
                <Heading level={5}>Campaign</Heading>
                <Text variant="secondary" color="var(--dark-60)">
                  A themed set of posts over a date range
                </Text>
              </div>
            </div>
          )}
          <div className={Styles.option} onClick={handleCreateStrategy}>
            <div className={cx(Styles.icon, Styles.strategy)}>
              <AddStrategies size={20} color="var(--green)" />
            </div>
            <div className={Styles.label}>
              <Heading level={5}>Add Strategy</Heading>
              <Text variant="secondary" color="var(--dark-60)">
                A group of campaigns around a goal
              </Text>
            </div>
          </div>
        </div>
      </Modal.Content>
    </Modal.Root>
  )
})
